import type { PublishProgress } from '@volley/crucible-ipc-types/ipc-channels';

export type PublishProgressHandlers = {
  setMessage: (message: string) => void;
  logDeveloperMessage: (message: string) => void;
  setLocalFailedCheck: (failedCheck: string | null) => void;
  clearDeployFailureContext: () => void;
  markPostCiTriggered: () => void;
  isPostCiTriggered: () => boolean;
  markResolved: () => void;
  setDeployState: (state: 'deployed' | 'error') => void;
  setDeployFailureCardEnabled: (enabled: boolean) => void;
  setDeployErrorCode: (code: string | null) => void;
  openProtoHub: (gameId: string) => void;
  invalidateDeployUpToDate: () => void;
  refetchDeployFailure: () => void;
};

export type PostDeployProtoHubRoute = {
  gameId: string;
  workspaceGameId: string | undefined;
  deployAttemptKey: string;
  openedForDeployAttemptKey: string | null;
  nativeSimulatorAvailable: boolean;
  hostBridgeAvailable: boolean;
  openHostBridge: () => void;
  openNative: () => void;
};

// `tests` is overloaded with the local pre-flight and CI quality gate, so only
// these unambiguous post-CI steps may arm the remote failure card.
const POST_CI_STEPS: ReadonlySet<PublishProgress['step']> = new Set([
  'ci-triggered',
  'build-image',
  'deploy',
  'verify',
  'done',
]);

export function shouldHandleWorkspacePublishProgress(
  event: Pick<PublishProgress, 'gameId'>,
  workspaceGameId: string | undefined,
): boolean {
  return !!workspaceGameId && event.gameId === workspaceGameId;
}

export function applyWorkspacePublishProgress(
  event: PublishProgress,
  workspaceGameId: string | undefined,
  handlers: PublishProgressHandlers,
): void {
  if (!shouldHandleWorkspacePublishProgress(event, workspaceGameId)) return;

  if (event.message) {
    if (event.audience === 'dev') {
      handlers.logDeveloperMessage(event.message);
    } else {
      handlers.setMessage(event.message);
    }
  }

  if (event.failedCheck) {
    handlers.setLocalFailedCheck(event.failedCheck);
    // A local-only failure must not let a prior attempt's remote failed-job
    // cache override its current failed-check chip.
    handlers.clearDeployFailureContext();
  }

  if (POST_CI_STEPS.has(event.step)) {
    handlers.markPostCiTriggered();
  }

  if (event.complete) {
    handlers.markResolved();
    handlers.setDeployState('deployed');
    handlers.setDeployFailureCardEnabled(false);
    handlers.setLocalFailedCheck(null);
    handlers.setDeployErrorCode(null);
    handlers.openProtoHub(event.gameId);
    // The successful deploy changed HEAD, so any pre-deploy cache verdict is stale.
    handlers.invalidateDeployUpToDate();
  }

  if (event.error) {
    handlers.markResolved();
    handlers.setMessage(event.error);
    handlers.setDeployState('error');
    // Pre-flight errors keep their legacy message surface. Remote failure
    // context is meaningful only after the publish pipeline reached CI.
    if (handlers.isPostCiTriggered()) {
      handlers.setDeployFailureCardEnabled(true);
      handlers.refetchDeployFailure();
    }
  }
}

export function routePostDeployProtoHub(route: PostDeployProtoHubRoute): string | null {
  if (!shouldHandleWorkspacePublishProgress(route, route.workspaceGameId)) {
    return route.openedForDeployAttemptKey;
  }
  if (route.openedForDeployAttemptKey === route.deployAttemptKey) {
    return route.openedForDeployAttemptKey;
  }

  if (!route.nativeSimulatorAvailable && route.hostBridgeAvailable) {
    route.openHostBridge();
  } else {
    route.openNative();
  }
  return route.deployAttemptKey;
}
